import { useSubscription } from '../hooks/useSubscription';
import { redirectToBillingPortal } from '../services/stripeService';
import { CreditCard, Calendar, AlertCircle, CheckCircle } from 'lucide-react';
import { useState } from 'react';

export function SubscriptionDashboard() {
  const { currentTier, subscriptionStatus, hasSetupFee, loading, error } = useSubscription();
  const [redirecting, setRedirecting] = useState(false);

  const handleManageBilling = async () => {
    try {
      setRedirecting(true);
      await redirectToBillingPortal();
    } catch (err) {
      console.error('Failed to redirect to billing portal:', err);
      setRedirecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <AlertCircle className="inline mr-2" size={20} />
        Error loading subscription data. Please try again.
      </div>
    );
  }

  const isActive = subscriptionStatus?.subscription_status === 'active';
  const isCanceling = subscriptionStatus?.cancel_at_period_end;
  const currentPeriodEnd = subscriptionStatus?.current_period_end
    ? new Date(subscriptionStatus.current_period_end).toLocaleDateString()
    : null;

  const hasNoSubscription = currentTier === null || !hasSetupFee;

  return (
    <div className="space-y-6">
      {hasNoSubscription ? (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-md p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <CreditCard className="text-blue-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to FutureLens Pro!</h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {!hasSetupFee
              ? 'Complete your one-time setup payment to unlock all features and choose your subscription plan.'
              : 'Choose a subscription plan to get started with your career development journey.'}
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => window.location.href = '/subscription?tab=plans'}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              {!hasSetupFee ? 'Complete Setup Payment' : 'Choose a Plan'}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Subscription Overview</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <CreditCard className="text-blue-600 mr-2" size={20} />
                <h3 className="font-semibold text-gray-900">Current Plan</h3>
              </div>
              <p className="text-2xl font-bold text-blue-600 capitalize">
                {currentTier || 'No Active Plan'}
              </p>
              <div className="mt-2 flex items-center">
                {isActive ? (
                  <>
                    <CheckCircle className="text-green-600 mr-1" size={16} />
                    <span className="text-sm text-green-600">Active</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="text-yellow-600 mr-1" size={16} />
                    <span className="text-sm text-yellow-600">Inactive</span>
                  </>
                )}
              </div>
            </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <Calendar className="text-blue-600 mr-2" size={20} />
              <h3 className="font-semibold text-gray-900">Billing Period</h3>
            </div>
            {currentPeriodEnd ? (
              <>
                <p className="text-lg text-gray-700">
                  {isCanceling ? 'Cancels on' : 'Renews on'}
                </p>
                <p className="text-xl font-semibold text-gray-900">{currentPeriodEnd}</p>
              </>
            ) : (
              <p className="text-sm text-gray-500">No active billing period</p>
            )}
          </div>
        </div>

        {!hasSetupFee && (
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="text-yellow-600 mr-2 flex-shrink-0" size={20} />
              <div>
                <h4 className="font-semibold text-yellow-900">Setup Fee Required</h4>
                <p className="text-sm text-yellow-800 mt-1">
                  Complete your setup fee payment to unlock all features.
                </p>
              </div>
            </div>
          </div>
        )}

        {isCanceling && (
          <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="text-orange-600 mr-2 flex-shrink-0" size={20} />
              <div>
                <h4 className="font-semibold text-orange-900">Subscription Canceling</h4>
                <p className="text-sm text-orange-800 mt-1">
                  Your subscription will be canceled on {currentPeriodEnd}. You can reactivate
                  it at any time before this date.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={handleManageBilling}
            disabled={redirecting}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {redirecting ? 'Redirecting...' : 'Manage Billing'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
